import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FileText, Heart, Quote, Newspaper, Wand2 } from 'lucide-react';
import { FocusHeader } from '../components/FocusHeader';

type VariantType = 'text_only' | 'personal_story' | 'summary' | 'full_article';

interface VariantOption {
  id: VariantType;
  title: string;
  description: string;
  icon: typeof FileText;
  preview: React.ReactNode;
}

export default function CampaignVariantSelection() {
  const navigate = useNavigate();
  const location = useLocation();
  const blogId = location.state?.blogId;
  const [selectedVariant, setSelectedVariant] = useState<VariantType | null>(null);

  if (!blogId) {
    navigate('/campaigns');
    return null;
  }

  const variants: VariantOption[] = [
    {
      id: 'text_only',
      title: 'Alleen Tekst',
      description: 'Pure tekst email zonder afbeeldingen of blog teaser',
      icon: FileText,
      preview: (
        <div className="space-y-2">
          <div className="h-2 bg-gray-200 rounded w-full animate-pulse" />
          <div className="h-2 bg-gray-200 rounded w-11/12 animate-pulse" />
          <div className="h-2 bg-gray-200 rounded w-9/12 animate-pulse" />
          <div className="h-2.5 bg-gray-300 rounded w-24 mt-2 animate-pulse" />
        </div>
      ),
    },
    {
      id: 'personal_story',
      title: 'Persoonlijk Verhaal',
      description: 'Email met authentieke anekdote die relateert aan het onderwerp',
      icon: Heart,
      preview: (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Heart className="w-3 h-3 text-red-400 shrink-0" />
            <div className="h-2 bg-red-200 rounded w-16 animate-pulse" />
          </div>
          <div className="h-2 bg-gray-200 rounded w-full animate-pulse" />
          <div className="h-2 bg-gray-200 rounded w-10/12 animate-pulse" />
          <div className="h-2.5 bg-gray-300 rounded w-24 mt-2 animate-pulse" />
        </div>
      ),
    },
    {
      id: 'summary',
      title: 'Samenvatting met Quote',
      description: 'Email met impactvolle quote als centraal element',
      icon: Quote,
      preview: (
        <div className="space-y-2">
          <div className="h-2 bg-gray-200 rounded w-10/12 animate-pulse" />
          <div className="border-l-4 border-[#00B8A9] pl-2 py-1.5 bg-[#00B8A9]/5">
            <div className="h-2 bg-[#00B8A9]/30 rounded w-full animate-pulse mb-1" />
            <div className="h-2 bg-[#00B8A9]/30 rounded w-9/12 animate-pulse" />
          </div>
          <div className="h-2 bg-gray-200 rounded w-11/12 animate-pulse" />
        </div>
      ),
    },
    {
      id: 'full_article',
      title: 'Volledige Blog Teaser',
      description: 'Email met featured image, blog titel en intro als teaser',
      icon: Newspaper,
      preview: (
        <div className="border border-gray-200 rounded-lg p-2 bg-gray-50">
          <div className="w-full h-10 bg-gray-300 rounded mb-2 animate-pulse" />
          <div className="h-2.5 bg-gray-300 rounded w-3/4 animate-pulse mb-1.5" />
          <div className="h-2 bg-gray-200 rounded w-full animate-pulse mb-1" />
          <div className="h-2 bg-gray-200 rounded w-10/12 animate-pulse" />
        </div>
      ),
    },
  ];

  const handleContinue = (variant: VariantType) => {
    navigate('/campaign/create-from-blog/generate', {
      state: { blogId, variant },
    });
  };

  return (
    <>
      <FocusHeader
        title="Kies email variant"
        subtitle="Selecteer hoe je de blog wilt presenteren in de email"
        onBack={() => navigate('/campaign/create-from-blog')}
      />
      <main className="flex-1 overflow-y-auto bg-[#F2F2EF] p-6">
        <div className="space-y-6 max-w-6xl mx-auto">
          <div className="card space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Kies variant
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {variants.map((variant) => {
                  const Icon = variant.icon;
                  const isSelected = selectedVariant === variant.id;

                  return (
                    <div key={variant.id} className="relative">
                      <button
                        onClick={() => setSelectedVariant(variant.id)}
                        aria-pressed={isSelected}
                        className={`
                          w-full relative text-left rounded-lg border-2 transition-all overflow-hidden
                          ${isSelected
                            ? 'border-[#00B8A9] bg-[#00B8A9]/5 shadow-lg ring-2 ring-[#00B8A9]/20'
                            : 'border-gray-200 bg-white hover:border-[#00B8A9]/50 hover:shadow-md'
                          }
                        `}
                      >
                        <div className="p-4">
                          <div className="flex items-start gap-3">
                            <div className="p-2 rounded-lg bg-gray-100 text-gray-600 shrink-0">
                              <Icon className="w-5 h-5" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <h3 className="font-semibold text-[#231F20] mb-0.5">
                                {variant.title}
                              </h3>
                              <p className="text-sm text-gray-600">
                                {variant.description}
                              </p>
                            </div>
                          </div>

                          <div className="bg-white border border-gray-200 rounded-lg p-3 mt-3 h-28 overflow-hidden">
                            <div className="text-xs text-gray-500 mb-2 font-medium">
                              Voorbeeld
                            </div>
                            {variant.preview}
                          </div>
                        </div>
                        {isSelected && (
                          <div className="absolute top-3 right-3 bg-[#00B8A9] text-white px-3 py-1 rounded-full text-xs font-medium shadow-lg">
                            Geselecteerd
                          </div>
                        )}
                      </button>

                      {isSelected && (
                        <button
                          onClick={() => handleContinue(variant.id)}
                          className="mt-3 w-full btn-primary inline-flex items-center justify-center gap-2 py-3"
                        >
                          <Wand2 className="w-5 h-5" />
                          Doorgaan met genereren
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  );
}
